import "./spells.page.css";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import CenteredContainer from "../../components/layout/CenteredContainer/CenteredContainer";
import SaveToast from "../../components/layout/SaveToast/SaveToast";
import PageLoadState from "../../components/layout/PageLoadState/PageLoadState";
import SpellPickerModal from "./SpellPickerModal";
import SpellLevelSummary from "./SpellLevelSummary";
import SpellcastingItems from "./SpellcastingItems";
import SpellTable from "./SpellTable";
import { SERVICE_URL } from "../../clients/app.const";
import { fetchGet } from "../../clients/response";
import { saveCharacter } from "../../clients/characters.client";
import { CORE_SPELLS } from "../../catalogs/spellsCatalog";
import { mapJsonToCharacter } from "../../models/Character";
import { useCharacterAutoSave } from "../../hooks/useCharacterAutoSave";
import { useSelector } from "react-redux";
import createId from "../../utils/createId";

const catalogSpellIds = new Set(CORE_SPELLS.map((spell) => spell.id));

const normalizeCatalogSpells = (character) => ({
  ...character,
  spellBook: {
    ...character.spellBook,
    spells: (character.spellBook.spells || []).map((spell) => (
      catalogSpellIds.has(spell.catalogId) ? { id: spell.id, catalogId: spell.catalogId } : spell
    )),
  },
});

const requiresCatalogSpellCleanup = (character) => (character.spellBook.spells || []).some((spell) => (
  catalogSpellIds.has(spell.catalogId) && Object.keys(spell).some((key) => key !== "id" && key !== "catalogId")
));
const SpellsPage = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const { tokenValue, user } = useSelector((state) => state.auth);
  const selectedCharacterId = useSelector((state) => state.character.selectedCharacterId);
  const [character, setCharacter] = useState();
  const [loadState, setLoadState] = useState("loading");
  const [reloadKey, setReloadKey] = useState(0);
  const [isSpellPickerOpen, setIsSpellPickerOpen] = useState(false);
  const { dismissSaveMessage, isSaveError, retrySave, saveMessage } =
    useCharacterAutoSave(character, tokenValue);

  useEffect(() => {
    let cancelled = false;

    const loadCharacter = async () => {
      if (!selectedCharacterId) {
        setCharacter(undefined);
        setLoadState("empty");
        return;
      }

      setLoadState("loading");
      const response = await fetchGet(
        `${SERVICE_URL}/characters/${selectedCharacterId}`,
        {},
        tokenValue
      );

      if (cancelled) return;
      if (response.isError || !response.data) {
        setCharacter(undefined);
        setLoadState("error");
        return;
      }

      try {
        const mappedCharacter = mapJsonToCharacter(response.data);
        const normalizedCharacter = normalizeCatalogSpells(mappedCharacter);
        if (requiresCatalogSpellCleanup(mappedCharacter)) {
          await saveCharacter(normalizedCharacter, tokenValue);
        }
        if (!cancelled) {
          setCharacter(normalizedCharacter);
          setLoadState("ready");
        }
      } catch (error) {
        console.error("Failed to process character data:", error);
        if (!cancelled) {
          setCharacter(undefined);
          setLoadState("error");
        }
      }
    };

    loadCharacter();
    return () => {
      cancelled = true;
    };
  }, [selectedCharacterId, tokenValue, user, reloadKey]);
  const addCatalogSpell = (spell) => {
    if (character.spellBook.spells.some((knownSpell) => knownSpell.catalogId === spell.id)) return;

    setCharacter((current) => ({
      ...current,
      spellBook: {
        ...current.spellBook,
        spells: [...current.spellBook.spells, { id: createId(), catalogId: spell.id }],
      },
    }));
    setIsSpellPickerOpen(false);
  };

  const removeSpell = (spellId) => {
    setCharacter((current) => ({
      ...current,
      spellBook: {
        ...current.spellBook,
        spells: current.spellBook.spells.filter((spell) => spell.id !== spellId),
      },
    }));
  };
  const updateSpellcastingItems = (items) => {
    setCharacter((current) => ({
      ...current,
      inventory: {
        ...current.inventory,
        items,
      },
    }));
  };
  const updateSpellLevels = (spellLevels) => {
    setCharacter((current) => ({
      ...current,
      spellBook: {
        ...current.spellBook,
        spellLevels,
      },
    }));
  };

  return (
    <CenteredContainer>
      <PageLoadState
        state={loadState}
        message={
          loadState === "loading"
            ? t("spells.loading")
            : loadState === "empty"
              ? t("spells.selectCharacter")
              : t("spells.loadError")
        }
        onRetry={() => setReloadKey((key) => key + 1)}
      />
      {loadState === "ready" && character && (
        <div className="spells-container">
          <SaveToast
            isError={isSaveError}
            message={saveMessage}
            onDismiss={dismissSaveMessage}
            onRetry={retrySave}
          />
          <div className="spell-page-layout">
            <section className="spell-list-panel">
              <div className="spell-search-container">
                <input
                  aria-label={t("spells.search")}
                  type="search"
                  placeholder={t("spells.search")}
                  className="spell-search-input"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
                <button
                  aria-label={t("spells.choose")}
                  className="add-skill-button"
                  type="button"
                  onClick={() => setIsSpellPickerOpen(true)}
                >
                  +
                </button>
              </div>
              <SpellPickerModal
                isOpen={isSpellPickerOpen}
                onClose={() => setIsSpellPickerOpen(false)}
                selectedSpells={character.spellBook.spells}
                onSelect={addCatalogSpell}
                character={character}
              />
              <SpellTable
                searchTerm={searchTerm}
                character={character}
                onRemove={removeSpell}
              />
            </section>
            <aside className="spell-page-sidebar">
              <SpellLevelSummary
                levels={character.spellBook.spellLevels}
                onChange={updateSpellLevels}
              />
              <SpellcastingItems
                items={character.inventory.items}
                onChange={updateSpellcastingItems}
              />
            </aside>
          </div>
        </div>
      )}
    </CenteredContainer>
  );
};

export default SpellsPage;
